import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActiveSelection,
  type FabricObject,
} from '@meitu/whee-infinite-canvas';
import {
  getOfficialAssetCategoryList,
  getOfficialAssetGenreList,
  getOfficialAssetMaterialList,
  getOfficialAssetTagList,
} from '$/api/canvasRquest';
import type {
  OfficialAssetServerCategory,
  OfficialAssetServerGenre,
  OfficialAssetServerImage,
  OfficialAssetServerMaterial,
  OfficialAssetServerTagGroup,
} from '$/api/server.types';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import type { MyIpItem } from '$/domains/myIp/types';
import useCanvasStore from '$/store/useCanvasStore';
import {
  insertDynamicImage,
  insertImage,
  setCurrentObjects,
} from '$/store/useCanvasObjectStore';
import useTeamCanvasLockStore from '$/store/useTeamCanvasLockStore';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getImageExtensionFromUrl } from '$/utils';

import MyIpAssetContent from '@/components/AssetModalContent/MyIpAssetContent';
import CategoryTabs from './CategoryTabs';
import MaterialDetailPanel from './MaterialDetailPanel';
import MaterialList from './MaterialList';
import SubjectRail from './SubjectRail';
import TagFilterBar from './TagFilterBar';
import styles from './index.module.scss';
import type {
  OfficialAssetMaterialImage,
  OfficialAssetMaterialImageKey,
  OfficialAssetSelectedTags,
} from './types';
import type {
  AssetModalInsertTarget,
  AssetModalListItem,
  AssetModalRawItem,
  OfficialAssetPopupTrackLocation,
} from '../type';
import { mergeOfficialAssetImagesAndUpload } from '../utils';
import {
  resolveAssetModalTrackRoomId,
  trackRoboneoAssetOfficialApplyClick,
  trackRoboneoAssetOfficialCardClick,
  trackRoboneoAssetOfficialCardShow,
  trackRoboneoAssetOfficialPopShow,
  trackRoboneoAssetOfficialSearchClick,
  trackRoboneoAssetOfficialSearchResult,
  trackRoboneoOfficialAssetNodeCreate,
} from '../tracking';

const OFFICIAL_ASSET_PAGE_SIZE = 50;
const OFFICIAL_ASSET_SCROLL_LOAD_THRESHOLD = 400;
const OFFICIAL_ASSET_CANVAS_IMAGE_GAP = 20;
const OFFICIAL_ASSET_SCENE_CATEGORY_CODE = 'roboneo_scene_asset_genre';
type OfficialAssetServerImageKey = Exclude<OfficialAssetMaterialImageKey, 'merged_image'>;
type OfficialAssetTranslatedServerImageKey = Exclude<OfficialAssetServerImageKey, 'nine_grid_image'>;

interface OfficialAssetContentProps {
  setOpenAssetPopover?: (open: boolean) => void;
  insertTarget?: AssetModalInsertTarget;
  officialAssetTrackLocation?: OfficialAssetPopupTrackLocation;
  closeOnBatchConfirm?: boolean;
  onAssetBatchConfirm?: (items: AssetModalListItem[]) => void;
  maxSelectableCount?: number;
  enableIpCategory?: boolean;
  onIpItemsAdd?: (items: MyIpItem[]) => void | Promise<void>;
  maxIpSelectableCount?: number;
  addLabel?: string;
  hideMaterials?: boolean;
}

/** 直接读取服务端素材对象中的图片字段，已有语言 key 的字段使用多语言文案。 */
function getOfficialAssetMaterialImages(
  material: OfficialAssetServerMaterial,
  getImageLabel: (key: OfficialAssetTranslatedServerImageKey) => string,
  nineGridImageLabel: string,
): OfficialAssetMaterialImage[] {
  return Object.entries(material).flatMap(([key, image]) => {
    if (!key.endsWith('_image') || image == null) {
      return [];
    }
    const sourceKey = key as OfficialAssetServerImageKey;
    return [{
      ...(image as OfficialAssetServerImage),
      sourceKey,
      label: sourceKey === 'nine_grid_image' ? nineGridImageLabel : getImageLabel(sourceKey),
    }];
  });
}

/** 将官方资产筛选项按数据平台 search_value 字段拼接。 */
function buildOfficialAssetSearchValue(selectedTags: OfficialAssetSelectedTags): string {
  return Object.entries(selectedTags).flatMap(([groupId, values]) => (
    values.map((value) => `${groupId}:${value}`)
  )).join(',');
}

/** 判断当前官方资产列表是否带有筛选条件。 */
function hasOfficialAssetSelectedTags(selectedTags: OfficialAssetSelectedTags): boolean {
  return Object.values(selectedTags).some((values) => values.length > 0);
}

/** 读取官方资产卡片在当前列表中的 0-based 位置。 */
function getOfficialAssetMaterialPositionId(
  materials: OfficialAssetServerMaterial[],
  material: OfficialAssetServerMaterial,
): number {
  const index = materials.findIndex((item) => item.id === material.id);
  return index >= 0 ? index : 0;
}

/** 从 Fabric 对象中读取节点 id，缺省时按空字符串上报。 */
function getOfficialAssetCanvasNodeId(object: FabricObject): string {
  const record = object as { id?: unknown; uuid?: unknown; };
  return String(record.id ?? record.uuid ?? '');
}

/** 把官方资产图片适配为外部托管资产项，供团队画布等宿主复用现有资产落节点逻辑。 */
function mapOfficialAssetImageToAssetModalItem({
  material,
  image,
}: {
  material: OfficialAssetServerMaterial;
  image: OfficialAssetMaterialImage;
}): AssetModalListItem {
  const name = image.sourceKey === 'merged_image'
    ? material.name
    : `${material.name}${image.label}`;
  const raw = {
    id: material.id,
    name,
    task_type: 'canvas',
    tool_type: 'official_asset_library',
    material_type: 'image',
    source_type: 'canvas',
    source_id: String(material.id),
    url: image.url,
    thumbnail_url: image.url,
    is_favorited: 0,
    created_at: 0,
    width: image.width,
    height: image.height,
    official_asset_material_id: material.id,
    official_asset_image_key: image.sourceKey,
  } as AssetModalRawItem;

  return {
    id: `${material.id}-${image.sourceKey}`,
    coverUrl: image.url,
    mediaUrl: image.url,
    materialType: 'image',
    aspectRatio: image.width / image.height,
    fileExtension: image.sourceKey === 'merged_image'
      ? 'png'
      : getImageExtensionFromUrl(image.url).extension as string,
    cardVariant: 'image',
    raw,
  };
}

/** 把单张官方资产图片插入画布，第一张走资产库默认落点，后续按指定坐标插到上一张下方。 */
async function insertOfficialAssetImageToCanvas({
  material,
  image,
  index,
  previousObject,
}: {
  material: OfficialAssetServerMaterial;
  image: OfficialAssetMaterialImage;
  index: number;
  previousObject: FabricObject | null;
}): Promise<FabricObject> {
  const suffix = image.sourceKey === 'merged_image'
    ? 'png'
    : getImageExtensionFromUrl(image.url).extension;
  const isDynamicImage = suffix === 'gif' || suffix === 'webp';
  const previousBounds = previousObject?.getBoundingRect();
  const commonParams = {
    url: image.url,
    uri: '',
    name: image.sourceKey === 'merged_image'
      ? material.name
      : `${material.name}${image.label}`,
    width: image.width,
    height: image.height,
    suffix,
    insertPos: index === 0 ? 'leftBottom' as const : 'point' as const,
    left: previousBounds?.left,
    top: previousBounds ? previousBounds.top + previousBounds.height + OFFICIAL_ASSET_CANVAS_IMAGE_GAP : undefined,
    extraData: {
      role: 'user',
      source: 'official_asset_library',
      asset_id: material.id,
      material_id: material.id,
      image_key: image.sourceKey,
    },
  };

  if (isDynamicImage) {
    return insertDynamicImage({
      ...commonParams,
      coverUrl: image.url,
      watermarkCoverUrl: undefined,
    });
  }

  return insertImage(commonParams);
}

/** 将官方资产本次插入的节点设置为画布当前选中态，多图使用 Fabric 多选对象。 */
function selectOfficialAssetCanvasObjects(objects: FabricObject[]): void {
  const { render } = useCanvasStore.getState();
  if (!render?._FC || objects.length === 0) {
    return;
  }

  if (objects.length === 1) {
    render._FC.setActiveObject(objects[0]);
  } else {
    const activeSelection = new ActiveSelection(objects, {
      canvas: render._FC,
    });
    render._FC.setActiveObject(activeSelection);
  }

  setCurrentObjects(objects);
  render._FC.requestRenderAll();
}

/**
 * 官方资产主体交互框架，托管一级分类、题材、标签、素材游标分页与详情状态。
 */
function OfficialAssetContent({
  setOpenAssetPopover,
  insertTarget = 'canvas',
  officialAssetTrackLocation,
  closeOnBatchConfirm = true,
  onAssetBatchConfirm,
  maxSelectableCount,
  enableIpCategory = false,
  onIpItemsAdd,
  maxIpSelectableCount,
  addLabel,
  hideMaterials,
}: OfficialAssetContentProps): React.ReactElement {
  const [ipSelected, setIpSelected] = useState(enableIpCategory);
  const modulesRef = useRef<HTMLDivElement>(null);
  const categoryFetchEpochRef = useRef(0);
  const selectedCategoryGenreRef = useRef('');
  const tagFetchEpochRef = useRef(0);
  const materialFetchEpochRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);
  const searchResultTotalRef = useRef<number | null>(null);
  const teamOid = useOfficialStore((state) => state.clientOId);
  const teamCanvasLockRole = useTeamCanvasLockStore((state) => state.role);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const [subjects, setSubjects] = useState<OfficialAssetServerCategory[]>([]);
  const [categories, setCategories] = useState<OfficialAssetServerGenre[]>([]);
  const [tagGroups, setTagGroups] = useState<OfficialAssetServerTagGroup[]>([]);
  const [materials, setMaterials] = useState<OfficialAssetServerMaterial[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<OfficialAssetServerCategory | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<OfficialAssetServerGenre | null>(null);
  const [selectedTags, setSelectedTags] = useState<OfficialAssetSelectedTags>({});
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState<OfficialAssetServerMaterial | null>(null);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialLoadingMore, setMaterialLoadingMore] = useState(false);
  const [materialCursor, setMaterialCursor] = useState('');
  const [materialHasMore, setMaterialHasMore] = useState(false);
  const [applyingToCanvas, setApplyingToCanvas] = useState(false);
  const [mergingToCanvas, setMergingToCanvas] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelectedMaterialIds, setBatchSelectedMaterialIds] = useState<Set<number>>(() => new Set());
  const selectedSubjectCode = selectedSubject?.code ?? '';
  const selectedCategoryGenreId = selectedCategory?.id ?? null;
  const selectedCategoryGenre = selectedCategory?.genre ?? '';
  const roomId = useMemo(() => resolveAssetModalTrackRoomId(insertTarget), [insertTarget]);
  const officialAssetSearchValue = useMemo(() => buildOfficialAssetSearchValue(selectedTags), [selectedTags]);
  const officialAssetTrackContext = useMemo(() => ({
    roomId,
    assetType: selectedSubjectCode,
    keyword: searchKeyword,
    searchValue: officialAssetSearchValue,
  }), [officialAssetSearchValue, roomId, searchKeyword, selectedSubjectCode]);
  const officialAssetTrackContextKey = useMemo(() => [
    roomId,
    selectedSubjectCode,
    selectedCategoryGenreId ?? '',
    searchKeyword,
    officialAssetSearchValue,
  ].join('|'), [
    officialAssetSearchValue,
    roomId,
    searchKeyword,
    selectedCategoryGenreId,
    selectedSubjectCode,
  ]);
  const materialListLoading = subjectsLoading || categoriesLoading || materialsLoading;
  const hasSelectedTags = useMemo(
    () => Object.values(selectedTags).some((values) => values.length > 0),
    [selectedTags],
  );
  const hasSearchKeyword = searchKeyword.length > 0;
  const selectedMaterialImages = useMemo(
    () => (selectedMaterial
      ? getOfficialAssetMaterialImages(
        selectedMaterial,
        (key) => languageStore.smartcatTranslationText((v) => v[key]),
        languageStore.smartcatTranslationText((v) => v.nine_grid_image),
      )
      : []),
    [languageStore, selectedMaterial],
  );
  const pickerMode = Boolean(onAssetBatchConfirm);
  const selectableLimit = maxSelectableCount == null
    ? Number.POSITIVE_INFINITY
    : Math.max(0, Math.floor(maxSelectableCount));
  const batchProcessingText = languageStore.smartcatTranslationText((v) => v.batched_processing);
  const quitBatchText = languageStore.smartcatTranslationText((v) => v.asset_quit_batch);
  const resolvedAddLabel = addLabel
    || languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_tool_add);
  const batchSelectedItems = useMemo(() => materials.flatMap((material) => {
    if (!batchSelectedMaterialIds.has(material.id)) return [];
    return getOfficialAssetMaterialImages(
      material,
      (key) => languageStore.smartcatTranslationText((v) => v[key]),
      languageStore.smartcatTranslationText((v) => v.nine_grid_image),
    ).map((image) => mapOfficialAssetImageToAssetModalItem({ material, image }));
  }), [batchSelectedMaterialIds, languageStore, materials]);
  const isTeamAccount = insertTarget === 'agentTeam' && teamOid != null && teamOid > 0;
  const teamCanvasOperationForbidden = isTeamAccount
    && teamCanvasLockRole !== 'idle'
    && teamCanvasLockRole !== 'holder';
  const applyDisabledReason = useMemo(() => {
    if (!onAssetBatchConfirm && insertTarget !== 'canvas' && insertTarget !== 'agentTeam') {
      return languageStore.smartcatTranslationText((v) => v.type_not_supported_on_canvas);
    }
    if (teamCanvasOperationForbidden) {
      return languageStore.smartcatTranslationText(
        (v) => v.you_currently_have_no_operation_permissions_please_contact_the_administrator_for_an_update_,
      );
    }
    if (!selectedMaterial) {
      return languageStore.smartcatTranslationText((v) => v.please_select_asset_first);
    }
    return '';
  }, [
    insertTarget,
    languageStore,
    onAssetBatchConfirm,
    selectedMaterial,
    teamCanvasOperationForbidden,
  ]);

  /** 选择器内切换官方普通素材的批量模式，退出时清空未确认内容。 */
  const toggleBatchMode = useCallback((): void => {
    setBatchSelectedMaterialIds(new Set());
    setBatchMode((previous) => !previous);
  }, []);

  /** 将一个官方素材包含的全部图片交给输入框宿主。 */
  const handleAddMaterial = useCallback((material: OfficialAssetServerMaterial): void => {
    if (!onAssetBatchConfirm) return;
    const items = getOfficialAssetMaterialImages(
      material,
      (key) => languageStore.smartcatTranslationText((v) => v[key]),
      languageStore.smartcatTranslationText((v) => v.nine_grid_image),
    ).map((image) => mapOfficialAssetImageToAssetModalItem({ material, image }));
    if (items.length === 0 || items.length > selectableLimit) return;
    onAssetBatchConfirm(items);
    if (closeOnBatchConfirm) setOpenAssetPopover?.(false);
  }, [closeOnBatchConfirm, languageStore, onAssetBatchConfirm, selectableLimit, setOpenAssetPopover]);

  /** 单个官方素材按其图片总数判断是否超过输入框剩余额度。 */
  const isMaterialAddDisabled = useCallback((material: OfficialAssetServerMaterial): boolean => (
    getOfficialAssetMaterialImages(
      material,
      (key) => languageStore.smartcatTranslationText((v) => v[key]),
      languageStore.smartcatTranslationText((v) => v.nine_grid_image),
    ).length > selectableLimit
  ), [languageStore, selectableLimit]);

  /** 批量模式按素材整组选中，并用图片总数约束输入框剩余额度。 */
  const handleBatchMaterialClick = useCallback((material: OfficialAssetServerMaterial): void => {
    const imageCount = getOfficialAssetMaterialImages(
      material,
      (key) => languageStore.smartcatTranslationText((v) => v[key]),
      languageStore.smartcatTranslationText((v) => v.nine_grid_image),
    ).length;
    setBatchSelectedMaterialIds((previous) => {
      const next = new Set(previous);
      if (next.has(material.id)) {
        next.delete(material.id);
        return next;
      }
      if (batchSelectedItems.length + imageCount <= selectableLimit) next.add(material.id);
      return next;
    });
  }, [batchSelectedItems.length, languageStore, selectableLimit]);

  /** 确认批量选择并关闭弹层，行为与个人资产的批量添加保持一致。 */
  const handleBatchAdd = useCallback((): void => {
    if (!onAssetBatchConfirm || batchSelectedItems.length === 0) return;
    onAssetBatchConfirm(batchSelectedItems);
    setBatchSelectedMaterialIds(new Set());
    setBatchMode(false);
    if (closeOnBatchConfirm) setOpenAssetPopover?.(false);
  }, [batchSelectedItems, closeOnBatchConfirm, onAssetBatchConfirm, setOpenAssetPopover]);

  const reportedOfficialCardShowKeysRef = useRef<Set<string>>(new Set());
  const materialTrackContextKeyRef = useRef('');

  /** 官方资产库首次展示默认类型及后续类型切换时分别上报一次弹框曝光。 */
  useEffect(() => {
    if (!selectedSubjectCode || !officialAssetTrackLocation) return;
    trackRoboneoAssetOfficialPopShow({
      roomId,
      assetType: selectedSubjectCode,
      location: officialAssetTrackLocation,
    });
  }, [officialAssetTrackLocation, roomId, selectedSubjectCode]);

  /** 把官方素材滚动区域恢复到顶部，配合筛选和分类切换后的首屏结果展示。 */
  const resetMaterialListPosition = useCallback((): void => {
    if (typeof window === 'undefined') {
      return;
    }
    window.requestAnimationFrame(() => {
      if (modulesRef.current) {
        modulesRef.current.scrollTop = 0;
      }
    });
  }, []);

  /** 初始化官方资产一级分类，并默认选中服务端返回的第一项。 */
  useEffect(() => {
    let active = true;

    /** 拉取一级分类列表，失败时保持空列表并提示用户。 */
    const loadSubjects = async (): Promise<void> => {
      setSubjectsLoading(true);
      try {
        const data = await getOfficialAssetCategoryList();
        if (!active) {
          return;
        }
        setSubjects(data.list);
        setSelectedSubject(data.list[0] ?? null);
      } catch {
        if (!active) {
          return;
        }
        setSubjects([]);
        setSelectedSubject(null);
        message.error(
          useLanguageStore.getState().languageStore
            .smartcatTranslationText((v) => v.material_load_fail),
        );
      } finally {
        if (active) {
          setSubjectsLoading(false);
        }
      }
    };

    loadSubjects().catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  /** 一级分类变化后拉取题材列表，优先保持用户已选题材，不存在时再回退第一项。 */
  useEffect(() => {
    categoryFetchEpochRef.current += 1;
    const fetchEpoch = categoryFetchEpochRef.current;
    setCategories([]);
    setSelectedCategory(null);
    setTagGroups([]);
    setMaterials([]);
    setMaterialCursor('');
    setMaterialHasMore(false);
    setSelectedMaterial(null);

    if (!selectedSubjectCode) {
      setCategoriesLoading(false);
      return;
    }

    /** 拉取当前一级分类下的题材列表，按题材业务标识恢复选择并触发后续请求。 */
    const loadCategories = async (): Promise<void> => {
      setCategoriesLoading(true);
      try {
        const data = await getOfficialAssetGenreList(selectedSubjectCode);
        if (fetchEpoch !== categoryFetchEpochRef.current) {
          return;
        }
        const nextCategory = data.list.find(
          (category) => category.genre === selectedCategoryGenreRef.current,
        ) ?? data.list[0] ?? null;
        setCategories(data.list);
        setSelectedCategory(nextCategory);
        selectedCategoryGenreRef.current = nextCategory?.genre ?? '';
      } catch {
        if (fetchEpoch !== categoryFetchEpochRef.current) {
          return;
        }
        setCategories([]);
        setSelectedCategory(null);
        message.error(
          useLanguageStore.getState().languageStore
            .smartcatTranslationText((v) => v.material_load_fail),
        );
      } finally {
        if (fetchEpoch === categoryFetchEpochRef.current) {
          setCategoriesLoading(false);
        }
      }
    };

    loadCategories().catch(() => {});
  }, [selectedSubjectCode]);

  /** 题材变化后拉取对应标签组；切换题材时不主动清空已选标签。 */
  useEffect(() => {
    tagFetchEpochRef.current += 1;
    const fetchEpoch = tagFetchEpochRef.current;
    setTagGroups([]);

    if (!selectedSubjectCode || !selectedCategoryGenre) {
      return;
    }

    /** 拉取当前题材对应的标签组，返回后复用已选标签值做展示命中。 */
    const loadTagGroups = async (): Promise<void> => {
      try {
        const data = await getOfficialAssetTagList({
          code: selectedSubjectCode,
          genre: selectedCategoryGenre,
        });
        if (fetchEpoch !== tagFetchEpochRef.current) {
          return;
        }
        setTagGroups(data.list);
      } catch {
        if (fetchEpoch !== tagFetchEpochRef.current) {
          return;
        }
        setTagGroups([]);
        message.error(
          useLanguageStore.getState().languageStore
            .smartcatTranslationText((v) => v.material_load_fail),
        );
      }
    };

    loadTagGroups().catch(() => {});
  }, [selectedCategoryGenre, selectedSubjectCode]);

  /** 题材、筛选或已提交关键词变化后拉取第一页素材，并在搜索态回传结果数。 */
  useEffect(() => {
    materialFetchEpochRef.current += 1;
    const fetchEpoch = materialFetchEpochRef.current;
    loadMoreInFlightRef.current = false;
    setMaterials([]);
    setMaterialCursor('');
    setMaterialHasMore(false);
    setSelectedMaterial(null);
    materialTrackContextKeyRef.current = '';
    searchResultTotalRef.current = null;

    if (!selectedCategoryGenreId) {
      setMaterialsLoading(false);
      setMaterialLoadingMore(false);
      return;
    }

    /** 拉取当前题材和筛选条件下的第一页素材列表。 */
    const loadMaterials = async (): Promise<void> => {
      setMaterialsLoading(true);
      setMaterialLoadingMore(false);
      try {
        const data = await getOfficialAssetMaterialList({
          genre_id: selectedCategoryGenreId,
          count: OFFICIAL_ASSET_PAGE_SIZE,
          filters: selectedTags,
          keyword: searchKeyword || undefined,
        });
        if (fetchEpoch !== materialFetchEpochRef.current) {
          return;
        }
        materialTrackContextKeyRef.current = officialAssetTrackContextKey;
        setMaterials(data.list);
        setSelectedMaterial(data.list[0] ?? null);
        setMaterialCursor(data.cursor);
        setMaterialHasMore(Boolean(data.cursor));
        searchResultTotalRef.current = searchKeyword ? data.total ?? null : null;
        if (searchKeyword || hasOfficialAssetSelectedTags(selectedTags)) {
          trackRoboneoAssetOfficialSearchResult(
            officialAssetTrackContext,
            data.total ?? data.list.length,
          );
        }
      } catch {
        if (fetchEpoch !== materialFetchEpochRef.current) {
          return;
        }
        setMaterials([]);
        setMaterialCursor('');
        setMaterialHasMore(false);
        message.error(
          useLanguageStore.getState().languageStore
            .smartcatTranslationText((v) => v.material_load_fail),
        );
      } finally {
        if (fetchEpoch === materialFetchEpochRef.current) {
          setMaterialsLoading(false);
        }
      }
    };

    loadMaterials().catch(() => {});
  }, [
    officialAssetTrackContext,
    officialAssetTrackContextKey,
    searchKeyword,
    selectedCategoryGenreId,
    selectedTags,
  ]);

  /** 搜索或筛选上下文变化后，允许同一素材在新结果集中重新上报曝光。 */
  useEffect(() => {
    reportedOfficialCardShowKeysRef.current.clear();
  }, [officialAssetTrackContextKey]);

  /** 角色 IP 目录展示时不统计后台加载的普通素材，切回素材目录后再按位置去重曝光。 */
  useEffect(() => {
    if ((enableIpCategory && ipSelected) || materialTrackContextKeyRef.current !== officialAssetTrackContextKey) {
      return;
    }
    materials.forEach((material, index) => {
      const trackKey = `${officialAssetTrackContextKey}:${material.id}:${index}`;
      if (reportedOfficialCardShowKeysRef.current.has(trackKey)) {
        return;
      }
      reportedOfficialCardShowKeysRef.current.add(trackKey);
      trackRoboneoAssetOfficialCardShow(material, officialAssetTrackContext, index);
    });
  }, [enableIpCategory, ipSelected, materials, officialAssetTrackContext, officialAssetTrackContextKey]);

  /** 切换一级分类时清空标签和素材状态，题材会在接口返回后优先恢复原选择。 */
  const handleSubjectChange = useCallback((subject: OfficialAssetServerCategory): void => {
    if (selectedSubject?.code === subject.code) {
      return;
    }
    setSelectedSubject(subject);
    setSelectedTags({});
    resetMaterialListPosition();
  }, [resetMaterialListPosition, selectedSubject]);

  /** 切换题材时保留已选标签值，只重置素材列表滚动位置。 */
  const handleCategoryChange = useCallback((category: OfficialAssetServerGenre): void => {
    if (selectedCategory?.id === category.id) {
      return;
    }
    selectedCategoryGenreRef.current = category.genre;
    setSelectedCategory(category);
    resetMaterialListPosition();
  }, [resetMaterialListPosition, selectedCategory]);

  /** 更新单个标签组的选择值，并触发素材列表按服务端筛选重新加载。 */
  const handleTagGroupChange = useCallback((groupId: string, values: string[]): void => {
    setSelectedTags((currentTags) => {
      const nextTags = { ...currentTags };
      if (values.length > 0) {
        nextTags[groupId] = values;
      } else {
        delete nextTags[groupId];
      }
      return nextTags;
    });
    resetMaterialListPosition();
  }, [resetMaterialListPosition]);

  /** 清空单个标签组，选中值归零后控件恢复未选择展示并重新加载列表。 */
  const handleClearTagGroup = useCallback((groupId: string): void => {
    setSelectedTags((currentTags) => {
      if (!currentTags[groupId]?.length) {
        return currentTags;
      }
      const nextTags = { ...currentTags };
      delete nextTags[groupId];
      return nextTags;
    });
    resetMaterialListPosition();
  }, [resetMaterialListPosition]);

  /** 搜索输入时只更新文本，回车或失焦后再提交服务端搜索。 */
  const handleSearchChange = useCallback((value: string): void => {
    setSearchValue(value);
  }, []);

  /** 提交搜索关键词，相同关键词不重复请求，并按当前筛选条件记录搜索点击。 */
  const handleSearch = useCallback((value: string): void => {
    const keyword = value.trim();
    if (keyword === searchKeyword) {
      return;
    }
    trackRoboneoAssetOfficialSearchClick({
      ...officialAssetTrackContext,
      keyword,
    });
    setSearchKeyword(keyword);
    searchResultTotalRef.current = null;
    resetMaterialListPosition();
  }, [officialAssetTrackContext, resetMaterialListPosition, searchKeyword]);

  /** 滚动到底部阈值内时使用服务端 cursor 拉取下一页素材。 */
  const handleLoadMoreMaterials = useCallback(async (): Promise<void> => {
    if (
      !selectedCategoryGenreId
      || !materialHasMore
      || !materialCursor
      || materialsLoading
      || materialLoadingMore
      || loadMoreInFlightRef.current
    ) {
      return;
    }

    const fetchEpoch = materialFetchEpochRef.current;
    loadMoreInFlightRef.current = true;
    setMaterialLoadingMore(true);
    try {
      const data = await getOfficialAssetMaterialList({
        genre_id: selectedCategoryGenreId,
        count: OFFICIAL_ASSET_PAGE_SIZE,
        cursor: materialCursor,
        filters: selectedTags,
        keyword: searchKeyword || undefined,
      });
      if (fetchEpoch !== materialFetchEpochRef.current) {
        return;
      }
      setMaterials((currentMaterials) => [...currentMaterials, ...data.list]);
      setMaterialCursor(data.cursor);
      setMaterialHasMore(Boolean(data.cursor));
      if (searchKeyword && data.total !== undefined) {
        searchResultTotalRef.current = data.total;
      }
    } catch {
      if (fetchEpoch === materialFetchEpochRef.current) {
        message.error(languageStore.smartcatTranslationText((v) => v.load_more_fail));
      }
    } finally {
      if (fetchEpoch === materialFetchEpochRef.current) {
        loadMoreInFlightRef.current = false;
        setMaterialLoadingMore(false);
      }
    }
  }, [
    materialCursor,
    materialHasMore,
    materialLoadingMore,
    materialsLoading,
    languageStore,
    searchKeyword,
    selectedCategoryGenreId,
    selectedTags,
  ]);

  /** 监听官方资产内容滚动，接近底部时触发滚动加载。 */
  const handleModulesScroll = useCallback((event: React.UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > OFFICIAL_ASSET_SCROLL_LOAD_THRESHOLD) {
      return;
    }
    handleLoadMoreMaterials().catch(() => {});
  }, [handleLoadMoreMaterials]);

  /** 点击素材卡片时更新右侧详情栏展示的素材，并上报官方卡片点击。 */
  const handleMaterialClick = useCallback((material: OfficialAssetServerMaterial): void => {
    trackRoboneoAssetOfficialCardClick(
      material,
      officialAssetTrackContext,
      getOfficialAssetMaterialPositionId(materials, material),
    );
    setSelectedMaterial(material);
  }, [materials, officialAssetTrackContext]);

  /** 将当前官方素材的全部图片串行插入画布，后续图片排列到上一张下方。 */
  const handleApplyMaterialToCanvas = useCallback(async (): Promise<void> => {
    if (applyingToCanvas || mergingToCanvas) {
      return;
    }
    if (applyDisabledReason) {
      message.warning(applyDisabledReason);
      return;
    }
    if (!selectedMaterial) {
      return;
    }

    trackRoboneoAssetOfficialApplyClick({
      material: selectedMaterial,
      context: officialAssetTrackContext,
      positionId: getOfficialAssetMaterialPositionId(materials, selectedMaterial),
      clickType: 'split',
      mediaUrl: selectedMaterialImages.map((image) => image.url).join(','),
    });
    setApplyingToCanvas(true);
    try {
      if (onAssetBatchConfirm) {
        onAssetBatchConfirm(selectedMaterialImages.map((image) => (
          mapOfficialAssetImageToAssetModalItem({ material: selectedMaterial, image })
        )));
        if (closeOnBatchConfirm) {
          setOpenAssetPopover?.(false);
        }
        return;
      }

      let previousObject: FabricObject | null = null;
      const insertedObjects: FabricObject[] = [];
      for (const [index, image] of selectedMaterialImages.entries()) {
        // eslint-disable-next-line no-await-in-loop -- 多图插入需等待上一张落点，才能把下一张放到其下方。
        previousObject = await insertOfficialAssetImageToCanvas({
          material: selectedMaterial,
          image,
          index,
          previousObject,
        });
        insertedObjects.push(previousObject);
        trackRoboneoOfficialAssetNodeCreate({
          roomId,
          nodeId: getOfficialAssetCanvasNodeId(previousObject),
          contentUrl: image.url,
          materialId: String(selectedMaterial.id),
        });
      }
      selectOfficialAssetCanvasObjects(insertedObjects);
      if (closeOnBatchConfirm) {
        setOpenAssetPopover?.(false);
      }
      message.success(languageStore.smartcatTranslationText((v) => v.asset_applied_to_canvas));
    } catch (error) {
      console.error('apply official asset to canvas failed:', error);
      message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
    } finally {
      setApplyingToCanvas(false);
    }
  }, [
    applyDisabledReason,
    applyingToCanvas,
    materials,
    mergingToCanvas,
    officialAssetTrackContext,
    languageStore,
    closeOnBatchConfirm,
    onAssetBatchConfirm,
    roomId,
    selectedMaterial,
    selectedMaterialImages,
    setOpenAssetPopover,
  ]);

  /** 将当前官方素材的全部图片合并并上传，再把唯一的合并图应用到画布。 */
  const handleMergeApplyMaterialToCanvas = useCallback(async (): Promise<void> => {
    if (applyingToCanvas || mergingToCanvas) {
      return;
    }
    if (applyDisabledReason) {
      message.warning(applyDisabledReason);
      return;
    }
    if (!selectedMaterial) {
      return;
    }

    trackRoboneoAssetOfficialApplyClick({
      material: selectedMaterial,
      context: officialAssetTrackContext,
      positionId: getOfficialAssetMaterialPositionId(materials, selectedMaterial),
      clickType: 'merge',
      mediaUrl: selectedMaterialImages.map((image) => image.url).join(','),
    });
    setMergingToCanvas(true);
    try {
      const mergedImage = await mergeOfficialAssetImagesAndUpload(selectedMaterial);
      const mergedMaterialImage: OfficialAssetMaterialImage = {
        ...mergedImage,
        sourceKey: 'merged_image',
        label: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_merge_layer),
      };

      if (onAssetBatchConfirm) {
        onAssetBatchConfirm([
          mapOfficialAssetImageToAssetModalItem({
            material: selectedMaterial,
            image: mergedMaterialImage,
          }),
        ]);
        if (closeOnBatchConfirm) {
          setOpenAssetPopover?.(false);
        }
        return;
      }

      const insertedObject = await insertOfficialAssetImageToCanvas({
        material: selectedMaterial,
        image: mergedMaterialImage,
        index: 0,
        previousObject: null,
      });
      trackRoboneoOfficialAssetNodeCreate({
        roomId,
        nodeId: getOfficialAssetCanvasNodeId(insertedObject),
        contentUrl: mergedMaterialImage.url,
        materialId: String(selectedMaterial.id),
      });
      selectOfficialAssetCanvasObjects([insertedObject]);
      if (closeOnBatchConfirm) {
        setOpenAssetPopover?.(false);
      }
      message.success(languageStore.smartcatTranslationText((v) => v.asset_applied_to_canvas));
    } catch (error) {
      console.error('merge official asset and apply to canvas failed:', error);
      message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
    } finally {
      setMergingToCanvas(false);
    }
  }, [
    applyingToCanvas,
    applyDisabledReason,
    closeOnBatchConfirm,
    languageStore,
    materials,
    mergingToCanvas,
    officialAssetTrackContext,
    onAssetBatchConfirm,
    roomId,
    selectedMaterial,
    selectedMaterialImages,
    setOpenAssetPopover,
  ]);

  return (
    <div className={styles.officialAssetContent}>
      <SubjectRail
        subjects={subjects}
        selectedSubjectCode={selectedSubject?.code ?? ''}
        onSubjectChange={(subject) => { setIpSelected(false); handleSubjectChange(subject); }}
        ipSelected={enableIpCategory && ipSelected}
        onSelectIp={enableIpCategory ? () => setIpSelected(true) : undefined}
      />
      {enableIpCategory && ipSelected ? (
        <MyIpAssetContent
          assetType="official"
          requireCanvas={insertTarget === 'agentTeam'}
          trackRoomId={resolveAssetModalTrackRoomId(insertTarget)}
          onIpItemsAdd={onIpItemsAdd}
          maxIpSelectableCount={maxIpSelectableCount}
          addLabel={addLabel}
          hideMaterials={hideMaterials}
        />
      ) : (
        <>
          <div ref={modulesRef} className={styles.officialAssetModules} onScroll={handleModulesScroll}>
            <div className={styles.officialAssetStickyHeader}>
              <CategoryTabs
                categories={categories}
                selectedCategoryId={selectedCategory?.id ?? null}
                searchValue={searchValue}
                onCategoryChange={handleCategoryChange}
                onSearchChange={handleSearchChange}
                onSearch={handleSearch}
              />
              <TagFilterBar
                tagGroups={tagGroups}
                selectedTags={selectedTags}
                onTagGroupChange={handleTagGroupChange}
                onClearTagGroup={handleClearTagGroup}
              />
              {pickerMode && (
                <div className={styles.officialAssetBatchToolbar}>
                  <button type="button" aria-pressed={batchMode} onClick={toggleBatchMode}>
                    <IconFont name={batchMode ? 'arrowRightBrackets' : 'multipleChoiceBold'} fontSize={14} />
                    {batchMode ? quitBatchText : batchProcessingText}
                  </button>
                </div>
              )}
            </div>
            <MaterialList
              materials={materials}
              loading={materialListLoading}
              loadingMore={materialLoadingMore}
              emptyStateType={hasSearchKeyword || hasSelectedTags ? 'search' : 'preparing'}
              onMaterialClick={batchMode ? handleBatchMaterialClick : handleMaterialClick}
              selectedMaterialId={selectedMaterial?.id}
              batchMode={batchMode}
              batchSelectedMaterialIds={batchSelectedMaterialIds}
              addLabel={resolvedAddLabel}
              onMaterialAdd={pickerMode ? handleAddMaterial : undefined}
              isMaterialAddDisabled={isMaterialAddDisabled}
            />
            {batchMode && (
              <div className={styles.officialAssetBatchFooter} role="toolbar">
                <span>{`${batchSelectedItems.length}/${Number.isFinite(selectableLimit) ? selectableLimit : batchSelectedItems.length}`}</span>
                <button type="button" disabled={batchSelectedItems.length === 0} onClick={handleBatchAdd}>
                  {resolvedAddLabel}
                </button>
              </div>
            )}
          </div>
          {!batchMode && (
            <MaterialDetailPanel
              material={selectedMaterial}
              images={selectedMaterialImages}
              showMergeApplyButton={!pickerMode && selectedSubjectCode !== OFFICIAL_ASSET_SCENE_CATEGORY_CODE}
              applyingToCanvas={applyingToCanvas}
              mergingToCanvas={mergingToCanvas}
              applyDisabledReason={applyDisabledReason}
              actionDisabled={pickerMode && selectedMaterialImages.length > selectableLimit}
              onApplyToCanvas={handleApplyMaterialToCanvas}
              onMergeApplyToCanvas={handleMergeApplyMaterialToCanvas}
              actionLabel={pickerMode ? resolvedAddLabel : undefined}
            />
          )}
        </>
      )}
    </div>
  );
}

export default OfficialAssetContent;
